// Custom hook for shallow routing - updating browser url without reloading the page - mainly used for url parameter updates
export const useShallowRouter = () => {
  // Function to navigate without refreshing the page, changing the URL path and parameters shallowly.
  // If maintainParams is true, merges new queryParams with existing ones.
  // Setting a queryParams value to undefined removes it from the URL.
  const push = (
    path: string,
    queryParams: Record<string, string | number | boolean | undefined> = {},
    maintainParams: boolean = false,
  ) => {
    // Initialize URLSearchParams with current URL parameters if maintainParams is true.
    const currentParams = new URLSearchParams(maintainParams ? window.location.search : '');
    // Iterate through queryParams to set or delete parameters.
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === undefined) {
        // Delete the parameter if its value is undefined.
        currentParams.delete(key);
      } else {
        // Convert and set the parameter value to a string.
        currentParams.set(key, String(value));
      }
    });
    // Construct the updated query string.
    const queryString = currentParams.toString();
    // Navigate to the new URL with the updated parameters without reload.
    window.history.pushState({}, '', queryString ? `${path}?${queryString}` : path);
  };

  // Function similar to 'push' but replaces the current state in history.
  // Useful for updating URL parameters without adding a new history entry.
  const replace = (
    path: string,
    queryParams: Record<string, string | number | boolean | undefined> = {},
    maintainParams: boolean = false,
  ) => {
    // Initialize URLSearchParams with current URL parameters if maintainParams is true.
    const currentParams = new URLSearchParams(maintainParams ? window.location.search : '');
    // Iterate through queryParams to set or delete parameters.
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === undefined) {
        // Delete the parameter if its value is undefined.
        currentParams.delete(key);
      } else {
        // Convert and set the parameter value to a string.
        currentParams.set(key, String(value));
      }
    });
    // Construct the updated query string.
    const queryString = currentParams.toString();
    // Replace the current URL with the new one with updated parameters.
    window.history.replaceState({}, '', queryString ? `${path}?${queryString}` : path);
  };

  return { push, replace };
};
